import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, StatTile, Select } from "../components/kr";
import { TrendUp, TrendDown } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Cell } from "recharts";

export default function PnL() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/pnl/${year}/${month}`).then(r => setD(r.data)).catch(() => setD(null));
  }, [year, month]);

  if (!d) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <div className="w-6 h-6 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
          <div className="text-sm font-medium text-zinc-500 tracking-wide">Loading financials...</div>
        </div>
      </div>
    );
  }

  const profit = d.net_profit >= 0;
  const subsExp = d.subscriptions_expense || 0;
  
  const chart = [
    { name: "Revenue", value: d.revenue, color: "rgb(16, 185, 129)" }, // Emerald 500
    { name: "COGS", value: d.cogs, color: "rgb(244, 63, 94)" },       // Rose 500
    { name: "Salaries", value: d.salaries, color: "rgb(245, 158, 11)" }, // Amber 500
    { name: "Fixed", value: d.fixed_expenses, color: "rgb(99, 102, 241)" }, // Indigo 500
    { name: "Variable", value: d.variable_expenses, color: "rgb(236, 72, 153)" }, // Pink 500
    { name: "Subs", value: subsExp, color: "rgb(6, 182, 212)" },       // Cyan 500
    { name: "Net Profit", value: Math.abs(d.net_profit), color: profit ? "rgb(16, 185, 129)" : "rgb(244, 63, 94)" },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8 bg-zinc-50/50 min-h-screen">
      <PageHeader
        label="FINANCIAL MANAGEMENT"
        title="Monthly P&L Statement"
        subtitle="Track cash flows, expenditures, and net performance dynamically."
        action={
          <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-zinc-200/80 shadow-sm">
            <Select value={month} onChange={e => setMonth(parseInt(e.target.value))} data-testid="pnl-month" className="!w-36 border-none bg-transparent focus:ring-0 font-medium text-zinc-700">
              {Array.from({length: 12}, (_,i)=>i+1).map(m => (
                <option key={m} value={m}>{new Date(0, m-1).toLocaleString("default", {month:"long"})}</option>
              ))}
            </Select>
            <div className="w-[1px] h-4 bg-zinc-200" />
            <Select value={year} onChange={e => setYear(parseInt(e.target.value))} data-testid="pnl-year" className="!w-24 border-none bg-transparent focus:ring-0 font-medium text-zinc-700">
              {[2024,2025,2026,2027].map(y => <option key={y}>{y}</option>)}
            </Select>
          </div>
        }
      />

      {/* Overview Metric Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile testId="pnl-revenue" label="Sales Revenue" value={`₹${d.revenue.toLocaleString("en-IN")}`} sub={`${d.sales_count} invoices generated`} accent="text-emerald-600 font-semibold" className="bg-white border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl" />
        <StatTile testId="pnl-cogs" label="Cost of Goods Sold" value={`₹${d.cogs.toLocaleString("en-IN")}`} sub={`${d.batches_count} inventory batches`} accent="text-rose-600 font-semibold" className="bg-white border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl" />
        <StatTile testId="pnl-gross" label="Gross Profit Margin" value={`₹${d.gross_profit.toLocaleString("en-IN")}`} accent={d.gross_profit >= 0 ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"} className="bg-white border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl" />
        <StatTile testId="pnl-net" label="Net Balance"
          value={`${profit ? "" : "− "}₹${Math.abs(d.net_profit).toLocaleString("en-IN")}`}
          accent={profit ? "text-emerald-600 font-bold" : "text-rose-600 font-bold"}
          sub={profit ? "Profitable Run" : "Operating Loss"} className="bg-white border border-zinc-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl" />
      </div>

      {/* Main Insights Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dynamic Chart Container */}
        <Card className="lg:col-span-2 !p-6 bg-white border border-zinc-200/60 rounded-2xl shadow-sm flex flex-col justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Financial Breakdown</div>
            <div className="text-lg font-semibold text-zinc-800 mb-6">Revenue Waterfall vs Expenses</div>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{top:10, right:5, left:-15, bottom:0}}>
                <CartesianGrid strokeDasharray="4 4" stroke="#f4f4f5" vertical={false}/>
                <XAxis dataKey="name" stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} dy={8}/>
                <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} dx={-4} tickFormatter={(val) => `₹${val >= 100000 ? (val/100000).toFixed(1) + 'L' : val}`} />
                <Tooltip 
                  cursor={{fill:"rgba(244,244,245,0.6)"}} 
                  contentStyle={{borderRadius:"12px", border:"1px solid #e4e4e7", background:"#fff", boxShadow:"0 4px 6px -1px rgb(0 0 0 / 0.1)", fontSize:13, padding:"10px 14px"}}
                  formatter={(value) => [`₹${value.toLocaleString("en-IN")}`, "Amount"]}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={45}>
                  {chart.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} className="transition-all duration-300 hover:opacity-85" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Breakdown Panel */}
        <Card className="!p-6 bg-white border border-zinc-200/60 rounded-2xl shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Cost Accounting</div>
          <div className="text-lg font-semibold text-zinc-800 mb-5">Expense Structures</div>
          
          <div className="space-y-4 text-sm text-zinc-600">
            {/* Salaries row */}
            <div className="flex justify-between items-center py-2 border-b border-zinc-100 hover:bg-zinc-50/50 px-1 rounded-lg transition-colors">
              <span className="font-medium text-zinc-700">Salaries & Payroll</span>
              <span className="font-semibold text-zinc-900">₹{d.salaries.toLocaleString("en-IN")}</span>
            </div>
            
            {/* Fixed Expenses breakdown */}
            <div className="bg-zinc-50/40 p-2.5 rounded-xl border border-zinc-100">
              <div className="flex justify-between items-center font-medium text-zinc-700 mb-1">
                <span>Fixed Expenses</span>
                <span className="font-semibold text-zinc-900">₹{d.fixed_expenses.toLocaleString("en-IN")}</span>
              </div>
              {d.fixed_breakdown?.map((f,i)=>(
                <div key={i} className="flex justify-between text-xs text-zinc-400 pt-1.5 pl-2 border-t border-dashed border-zinc-200/60 mt-1">
                  <span>{f.name}</span>
                  <span className="font-mono">₹{f.amount.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>

            {/* Variable Expenses row */}
            <div className="flex justify-between items-center py-2 border-b border-zinc-100 hover:bg-zinc-50/50 px-1 rounded-lg transition-colors">
              <span className="font-medium text-zinc-700">Variable / Miscellaneous</span>
              <span className="font-semibold text-zinc-900">₹{d.variable_expenses.toLocaleString("en-IN")}</span>
            </div>
            
            {/* Subscriptions breakdown */}
            <div className="bg-zinc-50/40 p-2.5 rounded-xl border border-zinc-100">
              <div className="flex justify-between items-center font-medium text-zinc-700 mb-1">
                <span>Subscriptions <span className="text-xs text-zinc-400 font-normal">(monthly-eq.)</span></span>
                <span className="font-semibold text-zinc-900">₹{subsExp.toLocaleString("en-IN")}</span>
              </div>
              {d.subscriptions_breakdown?.map((s,i)=>(
                <div key={i} className="flex justify-between text-xs text-zinc-400 pt-1.5 pl-2 border-t border-dashed border-zinc-200/60 mt-1">
                  <span>{s.name} <span className="text-[10px] bg-zinc-200/60 px-1.5 py-0.5 rounded text-zinc-500 font-sans ml-1 uppercase tracking-tight">{s.cycle}</span></span>
                  <span className="font-mono">₹{s.monthly_equivalent.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>

            {/* COGS Row */}
            <div className="flex justify-between items-center py-2 border-b border-zinc-100 hover:bg-zinc-50/50 px-1 rounded-lg transition-colors">
              <span className="font-medium text-zinc-700">Cost of Goods Sold (COGS)</span>
              <span className="font-semibold text-zinc-900">₹{d.cogs.toLocaleString("en-IN")}</span>
            </div>

            {/* Aggregate Totals block */}
            <div className="space-y-2 pt-4 border-t-2 border-zinc-900 mt-4">
              <div className="flex justify-between text-sm text-zinc-500 font-medium">
                <span>Total Operating Costs</span>
                <span className="text-zinc-800 font-semibold">₹{(d.cogs + d.salaries + d.fixed_expenses + d.variable_expenses + subsExp).toLocaleString("en-IN")}</span>
              </div>
              
              <div className={`flex justify-between items-center text-lg font-bold p-3 rounded-xl transition-all ${profit ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"}`}>
                <span className="text-sm uppercase tracking-wider font-semibold">Net Statement</span>
                <span>{profit ? "" : "− "}₹{Math.abs(d.net_profit).toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}